import { IonSkeletonText } from "@ionic/react";
import { useEffect } from "react";
import { FormattedMessage } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import Segment, { ButtonProps } from "@components/buttons/Segment";
import styles from "@components/carePlan/ExpertCarePlan.module.css";
import GoalEditorTextField from "@components/carePlan/GoalEditorTextField";
import GoalsManagerExpertTask from "@components/carePlan/GoalsManagerExpertTask";
import GoalsManagerMedia from "@components/carePlan/GoalsManagerMedia";
import GoalsManagerMemberTasks from "@components/carePlan/GoalsManagerMemberTasks";
import GoalsManagerTools from "@components/carePlan/GoalsManagerTools";
import Nbsp from "@components/Nbsp";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import useOpener from "@hooks/useOpener";
import { GoalSectionEnum } from "@typing/Enums";
import { GetJourneyWithPlanQuery, GoalStatusEnum } from "@typing/Generated";

type Journey = GetJourneyWithPlanQuery["journey"];
type GoalType = Journey["goals"][0];

type Props = {
  currentSection: GoalSectionEnum;
  goals: GoalType[];
  journey: Journey;
  loading: boolean;
  setCurrentSection: (section: GoalSectionEnum) => void;
};

type GoalProps = {
  currentSection: GoalSectionEnum;
  goal: GoalType;
  journey: Journey;
};

const Goal = ({ currentSection, goal, journey }: GoalProps) => {
  const titleOpener = useOpener();

  const isSelectedSection = (section: GoalSectionEnum) =>
    currentSection === section || currentSection === GoalSectionEnum.ALL;

  return (
    <div className={styles.goalEditor}>
      {titleOpener.isOpen ? (
        <div>
          <GoalEditorTextField
            canTemplate={false}
            defaultValue={goal?.title}
            goal={goal}
            itemBrowserOpener={null}
            journey={journey}
            onCancel={titleOpener.close}
            onSuccess={() => {
              titleOpener.close();
            }}
            templatePlaceholderKey="pages.carePlan.base.goals.searchPlaceholder"
          />
        </div>
      ) : (
        <div
          className={
            goal.status === GoalStatusEnum.DRAFT
              ? styles.goalEditorEditDraft
              : goal.status === GoalStatusEnum.EDIT_MODE
                ? styles.goalEditorEditMode
                : styles.goalEditorEdit
          }
        >
          <div data-test="goals-manager-title">
            <Heading2 color={goal.status === GoalStatusEnum.DRAFT ? "gray-300" : "white"}>
              {goal.title}
              {goal.status === GoalStatusEnum.EDIT_MODE && (
                <>
                  <Nbsp />
                  <BodyCopy color="white">
                    <FormattedMessage id="pages.carePlan.base.goals.editModeWrapped" />
                  </BodyCopy>
                </>
              )}
            </Heading2>
          </div>

          <ActionIconButton
            ariaLabelKey="accessibility.ariaLabels.edit"
            color={goal.status === GoalStatusEnum.DRAFT ? "gray-500" : "white"}
            data-test="goals-manager-title-edit"
            icon="edit"
            onClick={titleOpener.open}
          />
        </div>
      )}

      {isSelectedSection(GoalSectionEnum.CARE_PARTNER_TASKS) && (
        <GoalsManagerExpertTask currentSection={currentSection} goal={goal} journey={journey} />
      )}
      {isSelectedSection(GoalSectionEnum.MEMBER_TASKS) && (
        <GoalsManagerMemberTasks currentSection={currentSection} goal={goal} journey={journey} />
      )}
      {isSelectedSection(GoalSectionEnum.MEDIA) && (
        <GoalsManagerMedia currentSection={currentSection} goal={goal} journey={journey} />
      )}
      {isSelectedSection(GoalSectionEnum.TOOLS) && (
        <GoalsManagerTools currentSection={currentSection} goal={goal} journey={journey} />
      )}
    </div>
  );
};

const GoalsManager = ({ currentSection, goals, journey, loading, setCurrentSection }: Props) => {
  useEffect(() => {
    if (goals.length > 1 && currentSection === GoalSectionEnum.ALL) {
      setCurrentSection(GoalSectionEnum.CARE_PARTNER_TASKS);
    }
  }, [currentSection, goals.length, setCurrentSection]);

  const segmentButtons: ButtonProps<GoalSectionEnum> = [
    GoalSectionEnum.ALL,
    GoalSectionEnum.CARE_PARTNER_TASKS,
    GoalSectionEnum.MEMBER_TASKS,
    GoalSectionEnum.MEDIA,
    GoalSectionEnum.TOOLS
  ].map(section => ({
    disabled: section === GoalSectionEnum.ALL && goals.length > 1,
    maxWidth:
      section === GoalSectionEnum.CARE_PARTNER_TASKS
        ? 167
        : section === GoalSectionEnum.MEMBER_TASKS
          ? 142
          : section === GoalSectionEnum.ALL
            ? 115
            : section === GoalSectionEnum.MEDIA
              ? 80
              : 70,
    textKey: `models.goal.sections.${section}`,
    value: section
  }));

  return (
    <div>
      <Segment
        buttons={segmentButtons}
        fullWidth
        onChange={v => {
          setCurrentSection(v);
        }}
        value={currentSection}
      />
      <div className={styles.goalsManagerContainer}>
        {loading ? (
          <IonSkeletonText animated className={styles.loaderManager} />
        ) : (
          <div>
            {goals.map(goal => (
              <Goal currentSection={currentSection} goal={goal} journey={journey} key={goal.id} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalsManager;
